import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader, TrafficLayer } from '@react-google-maps/api';
import { useDispatch, useSelector } from 'react-redux';
import { Chip } from '@mui/material';

import { liveTrackinglayoutSelector } from '../../../features/layout/layoutSlice';
import { removeDetailView } from '../../../features/assets/assetsSlice';
import { removeEventDetailView, setViewType } from '../../../features/events/eventsSlice';
import { selectedTripSelector } from '../../../features/tripcenter/tripControlSlice';

import {
    zoomSelector,
    centerSelector,
    googleMapTypeIdSelector,
    setCenter,
    setZoom,
    setMapControl,
    resetFindLatLng,
    mapControlSelector,
    gensetViewportClipSelector,
} from '../../../features/map/mapSlice';
import {
    addZoneDraftCoords, addMarkerDraftCoords,
    zoneDraftMapControlSelector, markerDraftMapControlSelector,
    toggleZoneDrafting, toggleMarkerDrafting
} from '../../../features/places/placesSlice';
import { selectPlaceRoutePointOnClick, setPlaceRoutePointOnClick, setWorkingLatLng, getWorkingAddress } from '../../../features/tripcenter/googleRouteMakerSlice';
import { selectRouteMaker } from '../../../features/tripcenter/routeSlice';
import { googleMapTransaction } from '../../../features/user/userApi';
import { googleMapKeySourceSelector, useridSelector } from '../../../features/user/UserSlice';
import VehicleOverlays from '../VehicleOverlays';
import PlaceMarkerOverlay from '../PlaceMarkerOverlay';
import ZoneOverlay from '../ZoneOverlay';
import TracksOverlay from '../TracksOverlay';
import GensetTrackOverlay from '../GensetTrackOverlay';
import GoogleMapControl from './GoogleMapControl';
import GoogleEventMarker from './GoogleEventMarker';
import GoogleFindLatLngMarker from './GoogleFindLatLngMarker';
import ZoneCreate from './GoogleZoneCreate';
import MarkerCreate from './GoogleMarkerCreate';
import GoogleRouteMaker from './GoogleRouteMaker';
import GoogleRouteView from './GoogleRouteView';
import GoogleTripView from './GoogleTripView';

const offsetx = 100;
const offsety = 0;


const GoogleMapInstance = ({ libraries, googleMapKey, googleMapDevKey }) => {
    const dispatch = useDispatch();
    const dimentions = useSelector(liveTrackinglayoutSelector);
    const zoom = useSelector(zoomSelector);
    const center = useSelector(centerSelector);
    const mapTypeId = useSelector(googleMapTypeIdSelector);
    const [map, setMap] = useState(null);
    const googleMapKeySource = useSelector(googleMapKeySourceSelector);
    const userid = useSelector(useridSelector);
    const routeMaker = useSelector(selectRouteMaker);
    const mapControl = useSelector(mapControlSelector);
    const gensetViewportClip = useSelector(gensetViewportClipSelector);
    const selectedTrip = useSelector(selectedTripSelector);
    const zoneDraftMapControl = useSelector(zoneDraftMapControlSelector);
    const markerDraftMapControl = useSelector(markerDraftMapControlSelector);
    const previousCenterRef = useRef(null);
    const onLoad = useCallback(function callback(map) {
        setMap(map);
        window.googlemap = map;
        dispatch(googleMapTransaction({
            consumer: userid,
            provider: googleMapKeySource,
        }));
    }, [dispatch, googleMapKeySource, userid]);

    const onUnmount = useCallback(function callback(map) {
        setMap(null);
    }, []);

    const onMapClick = (e) => {
        const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };

        if (zoneDraftMapControl) {
            // Handle zone creation
            dispatch(addZoneDraftCoords(point));
        } else if (markerDraftMapControl) {
            // Handle marker creation
            dispatch(addMarkerDraftCoords(point));
        } else if (mapControl === 'findlatlng') {
            dispatch(setWorkingLatLng(point));
            dispatch(getWorkingAddress({ searchParam: point }));
        } else if (!['trip', 'route', 'track', 'genset'].includes(mapControl)) {
            // Only clear detail view if not in trip or route mode
            dispatch(removeDetailView());
            dispatch(setViewType('default'));
            dispatch(removeEventDetailView());
            dispatch(setMapControl('asset'));
        }
        dispatch(resetFindLatLng());
    };

    const setOffsetCenter = useCallback(
        (latlng) => {
            if (map) {
                if (map.getProjection()) {
                    const scale = Math.pow(2, map.getZoom());
                    const worldCoordinateCenter = map.getProjection().fromLatLngToPoint(latlng);
                    const pixelOffset = new window.google.maps.Point((offsetx / scale) || 0, (offsety / scale) || 0);
                    const worldCoordinateNewCenter = new window.google.maps.Point(
                        worldCoordinateCenter.x - pixelOffset.x,
                        worldCoordinateCenter.y + pixelOffset.y
                    );
                    const newCenter = map.getProjection().fromPointToLatLng(worldCoordinateNewCenter);
                    map.setCenter(newCenter);
                }
            }
        },
        [map],
    );

    useEffect(() => {
        const centerChanged = !previousCenterRef.current
            || previousCenterRef.current.lat !== center.lat
            || previousCenterRef.current.lng !== center.lng;
        previousCenterRef.current = { lat: center.lat, lng: center.lng };

        if (!centerChanged) return;
        if (mapControl === 'genset' || mapControl === 'track') return;
        if (window.google && window.google.maps) {
            const latlng = new window.google.maps.LatLng(center.lat, center.lng);
            setOffsetCenter(latlng);
        }

    }, [center, mapControl, setOffsetCenter])


    useEffect(() => {
        if (map) {
            map.setMapTypeId(mapTypeId);
        }
    }, [mapTypeId, map])


    //const isProduction = window.location.href.indexOf("v3.ilogistek.com");
    const isProduction = window.location.href.indexOf("ilogistek.com");
    const isBeta = window.location.href.indexOf("dev.dqw6ftwkytwa5.amplifyapp.com");
    const isDevelopment = window.location.href.indexOf("localhost");
    let mapCfg = {
        id: 'google-map-script',
        libraries,
        version: "3.58",
    };
    if (googleMapKey) {
        if (isProduction !== -1) {
            mapCfg = { ...mapCfg, googleMapsApiKey: googleMapKey };
        } else if (isBeta !== -1) {
            mapCfg = { ...mapCfg, googleMapsApiKey: googleMapKey };
        } else if (isDevelopment !== -1) {
            //do nothing
            mapCfg = { ...mapCfg, googleMapsApiKey: googleMapKey };
        }
    }
    const { isLoaded } = useJsApiLoader(mapCfg);
    const isTripDataLoading = mapControl === 'trip' && !!selectedTrip && !selectedTrip?.fullyLoaded;


    const useGensetClip = Boolean(gensetViewportClip)
        && (mapControl === 'genset' || mapControl === 'track');
    const clipInset = gensetViewportClip
        ? `inset(${gensetViewportClip.top}px ${gensetViewportClip.right}px ${gensetViewportClip.bottom}px ${gensetViewportClip.left}px round ${gensetViewportClip.radius || 0}px)`
        : undefined;
    const mapContainerStyle = {
        ...dimentions.main,
        ...(useGensetClip
            ? {
                clipPath: clipInset,
                WebkitClipPath: clipInset,
                zIndex: 20,
                pointerEvents: 'auto',
            }
            : {}),
    };

    const handleIdle = useCallback(() => {
        if (!map) return;
        if (!(mapControl === 'genset' || mapControl === 'track')) return;
        const nextCenter = map.getCenter();
        if (nextCenter) {
            const nextLat = nextCenter.lat();
            const nextLng = nextCenter.lng();
            const latDiff = Math.abs(nextLat - center.lat);
            const lngDiff = Math.abs(nextLng - center.lng);
            if (latDiff > 1e-7 || lngDiff > 1e-7) {
                dispatch(setCenter({ lat: nextLat, lng: nextLng }));
            }
        }
        const nextZoom = map.getZoom();
        if (Number.isFinite(nextZoom) && nextZoom !== zoom) {
            dispatch(setZoom(nextZoom));
        }
    }, [center.lat, center.lng, dispatch, map, mapControl, zoom]);

    return isLoaded ? (
        <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={zoom}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onIdle={handleIdle}
            options={{
                zoomControl: false,
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false,
                gestureHandling: useGensetClip ? 'greedy' : 'auto',
                draggable: true,
            }}
            onClick={onMapClick}
        >
            {mapControl === 'asset' && <TrafficLayer />}
            <VehicleOverlays />
            {map && <PlaceMarkerOverlay map={map} />}
            {map && <ZoneOverlay map={map} />}
            {map && <GoogleEventMarker />}
            {map && <TracksOverlay />}
            {map && <GensetTrackOverlay />}
            {map && <ZoneCreate />}
            {map && <MarkerCreate />}
            {map && <GoogleFindLatLngMarker map={map} />}
            {map && routeMaker === 'google' && <GoogleRouteMaker map={map} />}
            {map && <GoogleRouteView map={map} />}
            {map && <GoogleTripView map={map} />}
            <GoogleMapControl map={map} />
            {isTripDataLoading && (
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: '26px',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        pointerEvents: 'none'
                    }}
                >
                    <Chip
                        label="Loading Trip Data ..."
                        sx={{
                            height: 46,
                            px: 3,
                            borderRadius: '24px',
                            backgroundColor: '#F57C00',
                            color: '#FFFFFF',
                            fontWeight: 700,
                            fontSize: '1rem',
                            boxShadow: '0 8px 22px rgba(245,124,0,0.55)',
                            animation: 'tripLoadingPulse 1.35s ease-in-out infinite',
                            '@keyframes tripLoadingPulse': {
                                '0%': {
                                    transform: 'scale(1)',
                                    opacity: 0.95,
                                },
                                '50%': {
                                    transform: 'scale(1.07)',
                                    opacity: 1,
                                },
                                '100%': {
                                    transform: 'scale(1)',
                                    opacity: 0.95,
                                },
                            },
                            '& .MuiChip-label': {
                                px: 1,
                            },
                        }}
                    />
                </div>
            )}
        </GoogleMap>

    ) : <div></div>;
}

export default React.memo(GoogleMapInstance);
